import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

/** Options for the dimension value mapping state. */
interface DimensionValueMappingsOptions {
  /**
   * Merge mode: every selected dimension offers a target, however many values
   * it carries, and **no input is prefilled**.
   *
   * A merge names a target rather than editing a value, so the target field is
   * the intent: blank leaves the dimension a plain filter, a typed value makes
   * it the merge target. That is the only way to express "merge X, Y, Z into X
   * *for* these 100 brands", and it keeps one rule for one and many values —
   * Add/Rename keep their prefill, where editing the value *is* the operation.
   */
  allowMultiSource?: boolean;
}

/** Return shape for dimension value mapping state shared by Add/Rename/Merge. */
interface DimensionValueMappingsState {
  /** Current edited values keyed by dimension field. */
  newValues: Record<string, string>;
  /** Setter for edited values keyed by dimension field. */
  setNewValues: Dispatch<SetStateAction<Record<string, string>>>;
  /** Validation errors keyed by dimension field, plus `_global`. */
  errors: Record<string, string>;
  /** Setter for validation errors keyed by dimension field. */
  setErrors: Dispatch<SetStateAction<Record<string, string>>>;
  /** Dimensions offering a target input (single-value; any when multi-source). */
  mappingDims: [string, string[]][];
  /** Dimension mappings that express an operation (see `allowMultiSource`). */
  editedMappings: [string, string][];
  /** True when at least one dimension has a valid target value. */
  hasValidMapping: boolean;
  /** Reset mapping inputs and validation errors. */
  resetMappings: () => void;
}

/** Whether a selection offers a target mapping under the given mode. */
function offersMapping(
  values: string[] | undefined,
  allowMultiSource: boolean
): boolean {
  const count = values?.length ?? 0;
  return allowMultiSource ? count > 0 : count === 1;
}

/** Manages prefilled Add/Rename/Merge dimension-value edits from selections. */
export function useDimensionValueMappings(
  selections: Record<string, string[]>,
  { allowMultiSource = false }: DimensionValueMappingsOptions = {}
): DimensionValueMappingsState {
  const [newValues, setNewValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const previousPrefillsRef = useRef<Record<string, string>>({});

  useEffect(() => {
    setNewValues((prev) => {
      const next = { ...prev };
      const nextPrefills: Record<string, string> = {};
      for (const [key, values] of Object.entries(selections)) {
        if (!offersMapping(values, allowMultiSource)) {
          delete next[key];
          continue;
        }
        // Merge mode never prefills — the blank target is what marks the
        // dimension as a filter (see ``allowMultiSource``).
        if (allowMultiSource) continue;
        // Prefill with the selected value; re-prefill only when it changes so
        // a typed target survives selection tweaks.
        nextPrefills[key] = values[0];
        if (previousPrefillsRef.current[key] !== values[0]) {
          next[key] = values[0];
        }
      }
      for (const key of Object.keys(next)) {
        if (!selections[key]) delete next[key];
      }
      previousPrefillsRef.current = nextPrefills;
      // Keep the previous object when nothing changed, so an unstable
      // `selections` reference cannot re-render in a loop.
      const changed =
        Object.keys(next).length !== Object.keys(prev).length ||
        Object.entries(next).some(([key, value]) => prev[key] !== value);
      return changed ? next : prev;
    });
  }, [selections, allowMultiSource]);

  const mappingDims = useMemo(
    () =>
      Object.entries(selections).filter(([, vals]) =>
        offersMapping(vals, allowMultiSource)
      ) as [string, string[]][],
    [selections, allowMultiSource]
  );

  const editedMappings = useMemo(
    () =>
      Object.entries(newValues).filter(([key, val]) => {
        const sources = selections[key] ?? [];
        if (!offersMapping(sources, allowMultiSource)) return false;
        if (val.trim().length === 0) return false;
        // Merge: any typed target is the intent (a target equal to the only
        // selected value is caught as a field error, not silently ignored).
        // Add/Rename: the prefill must actually have been edited.
        return allowMultiSource || val.trim() !== sources[0];
      }) as [string, string][],
    [newValues, selections, allowMultiSource]
  );

  function resetMappings() {
    setNewValues({});
    setErrors({});
  }

  return {
    newValues,
    setNewValues,
    errors,
    setErrors,
    mappingDims,
    editedMappings,
    hasValidMapping: editedMappings.length > 0,
    resetMappings
  };
}
